import { FormatType } from './types'

// 将 Markdown 表格转换为二维数组
export const parseMarkdownTable = (mdTable: string): string[][] => {
  try {
    const lines = mdTable.trim().split('\n')
    if (lines.length < 3) {
      throw new Error('无效的 Markdown 表格')
    }

    // 移除表头分隔行 (第二行)
    const headerLine = lines[0]
    const rows = lines.slice(2)

    // 解析表头
    const headers = headerLine
      .trim()
      .replace(/^\||\|$/g, '')
      .split('|')
      .map(h => h.trim())

    // 解析数据行
    const data = rows.map(row => 
      row
        .trim()
        .replace(/^\||\|$/g, '')
        .split('|')
        .map(cell => cell.trim())
    )

    return [headers, ...data]
  } catch (error) {
    console.error('解析 Markdown 表格失败:', error)
    return []
  }
}

// 将 CSV 转换为二维数组
export const parseCSV = (csv: string): string[][] => {
  try {
    const lines = csv.trim().split('\n')
    return lines.map(line => 
      line.split(',').map(cell => cell.trim().replace(/^"(.*)"$/, '$1'))
    )
  } catch (error) {
    console.error('解析 CSV 失败:', error)
    return []
  }
}

// 将 JSON 转换为二维数组
export const parseJSON = (json: string): string[][] => {
  try {
    const data = JSON.parse(json)
    
    if (Array.isArray(data) && data.length > 0) {
      if (Array.isArray(data[0])) {
        // 二维数组格式
        return data
      } else if (typeof data[0] === 'object') {
        // 对象数组格式
        const headers = Object.keys(data[0])
        const rows = data.map(item => 
          headers.map(key => String(item[key] || ''))
        )
        return [headers, ...rows]
      }
    }
    throw new Error('不支持的 JSON 格式')
  } catch (error) {
    console.error('解析 JSON 失败:', error)
    return []
  }
}

// 将二维数组转换为 Markdown 表格
export const convertToMarkdown = (data: string[][]): string => {
  if (!data.length) return ''
  
  const headers = data[0]
  const separator = headers.map(() => '---')
  const rows = data.slice(1)
  
  const headerLine = `| ${headers.join(' | ')} |`
  const separatorLine = `| ${separator.join(' | ')} |`
  const rowLines = rows.map(row => `| ${row.join(' | ')} |`)
  
  return [headerLine, separatorLine, ...rowLines].join('\n')
}

// 将二维数组转换为 CSV
export const convertToCSV = (data: string[][]): string => {
  return data.map(row => 
    row.map(cell => {
      // 如果单元格包含逗号、双引号或换行符，则用双引号括起来
      if (/[",\n\r]/.test(cell)) {
        return `"${cell.replace(/"/g, '""')}"`
      }
      return cell
    }).join(',')
  ).join('\n')
}

// 将二维数组转换为 JSON
export const convertToJSON = (data: string[][], format: 'array' | 'object'): string => {
  if (!data.length) return '[]'
  
  if (format === 'array') {
    // 二维数组格式
    return JSON.stringify(data, null, 2)
  } else {
    // 对象数组格式
    const headers = data[0]
    const rows = data.slice(1)
    
    const result = rows.map(row => {
      const obj: Record<string, string> = {}
      headers.forEach((header, index) => {
        obj[header] = row[index] || ''
      })
      return obj
    })
    
    return JSON.stringify(result, null, 2)
  }
}

// 根据输入格式解析数据
export const parseInput = (input: string, format: FormatType): string[][] => {
  switch (format) {
    case 'markdown':
      return parseMarkdownTable(input)
    case 'csv':
      return parseCSV(input)
    case 'json':
      return parseJSON(input)
    default:
      return []
  }
}

// 根据输出格式转换数据
export const convertOutput = (data: string[][], format: FormatType, jsonFormat: 'array' | 'object' = 'object'): string => {
  switch (format) {
    case 'markdown':
      return convertToMarkdown(data)
    case 'csv':
      return convertToCSV(data)
    case 'json':
      return convertToJSON(data, jsonFormat)
    default:
      return ''
  }
} 